import React from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface TripCreationResponse {
  status: boolean;
  message: string;
  data: {
    id: number;
    tripCode: string;
    customer: any;
    operator: any;
    travelServiceDetail: any;
    startDatetime: string;
    sourceCity: string;
    destinationCities: string;
    tripStatus: string;
  };
}

interface TripSuccessDialogProps {
  open: boolean;
  onClose: () => void;
  tripCreationResponse: TripCreationResponse | null;
  onContinue?: () => void;
  onCreateAnother?: () => void; // Add new prop for create another action
  onNavigateToTrip?: (tripCode: string) => void; // Add navigation prop
  isOtherService?: boolean;
}

const TripSuccessDialog: React.FC<TripSuccessDialogProps> = ({
  open,
  onClose,
  tripCreationResponse,
  onContinue,
  onCreateAnother,
  onNavigateToTrip,
  isOtherService
}) => {
  const handleContinue = () => {
    if (onContinue) {
      onContinue();
    } else {
      onClose();
    }
  };

  const handleCreateAnother = () => {
    if (onCreateAnother) {
      onCreateAnother();
    } else {
      onClose();
    }
  };

  const handleViewTripDetails = () => {
    if (tripCreationResponse?.data?.tripCode && onNavigateToTrip) {
      onNavigateToTrip(tripCreationResponse.data.tripCode);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Disable backdrop click close
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1e1e1e',
          color: 'white',
          border: '2px solid #4caf50'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#4caf50', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <CheckCircleIcon />
        Trip Created Successfully!
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: '#1e1e1e', color: 'white', py: 3 }}>
        {tripCreationResponse && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#4caf50', textAlign: 'center', mb: 3 }}>
              {tripCreationResponse.message}
            </Typography>
            
            <Box sx={{ 
              p: 3, 
              backgroundColor: 'rgba(76, 175, 80, 0.1)', 
              borderRadius: 2,
              border: '1px solid rgba(76, 175, 80, 0.3)',
              textAlign: 'center',
              mb: 3
            }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Trip Code
              </Typography>
              <Typography variant="h4" sx={{ 
                color: '#4caf50', 
                fontWeight: 'bold',
                letterSpacing: 2,
                fontFamily: 'monospace'
              }}>
                {tripCreationResponse.data.tripCode}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Customer
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {tripCreationResponse.data.customer.name || 'Name not provided'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {tripCreationResponse.data.customer.mobileNumber}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Trip Status
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ color: '#ff9800' }}>
                  {tripCreationResponse.data.tripStatus}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Source
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {tripCreationResponse.data.sourceCity}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Destination
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {tripCreationResponse.data.destinationCities}
                </Typography>
              </Box>

              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Start Date & Time
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {new Date(tripCreationResponse.data.startDatetime).toLocaleString('en-IN')}
                </Typography>
              </Box>

              {/* {tripCreationResponse.data.travelServiceDetail && (
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Vehicle Details
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {tripCreationResponse.data.travelServiceDetail.carType?.makeCompany} {tripCreationResponse.data.travelServiceDetail.carType?.model}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Capacity: {tripCreationResponse.data.travelServiceDetail.carType?.capacity} passengers
                  </Typography>
                </Box>
              )} */}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: '#1e1e1e', p: 3, gap: 1 }}>
        {/* <Button
          onClick={handleCreateAnother}
          variant="outlined"
          sx={{ 
            color: '#4caf50',
            borderColor: '#4caf50',
            '&:hover': { 
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderColor: '#388e3c'
            },
            flex: 1
          }}
        >
          Create Another Trip
        </Button> */}
        
        <Button
          onClick={handleViewTripDetails}
          variant="contained"
          disabled={!!isOtherService}
          sx={{ 
            backgroundColor: '#4caf50',
            '&:hover': { backgroundColor: '#388e3c' },
            flex: 1
          }}
        >
          View Trip Details
        </Button>
        
        <Button
          onClick={handleContinue}
          variant="outlined"
          sx={{ 
            color: 'white',
            borderColor: 'white',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            minWidth: 'auto',
            px: 2
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TripSuccessDialog;
